// SharedSpaceView — top-level wrapper for the Shared Space surface on
// the web Dashboard. Composes:
//
//   1. SpaceCard         — create / join / manage (always)
//   2. SharedSpaceOverview — stats hero + calendar (only when in a space)
//   3. BillSplitPanel    — flatmate spaces only
//   4. BudgetProgressPanel — family spaces only
//
// Single source of truth for `spaceState` is fetched once at this level
// and shared with the children so the calendar gets member metadata
// and the conditional panel knows the type.

import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SpaceCard from "@/components/SpaceCard";
import SharedSpaceOverview from "@/components/SharedSpaceOverview";
import BillSplitPanel from "@/components/BillSplitPanel";
import BudgetProgressPanel from "@/components/BudgetProgressPanel";

export default function SharedSpaceView() {
  const { user } = useAuth() || {};
  const [spaceState, setSpaceState] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Load /api/analytics/spend so the family progress bar has a budget
  // anchor and the current-month spend total. Refetches whenever the
  // SpaceCard reports a mutation (create/join/leave/rotate).
  // We don't hardcode a budget fallback — analytics.budget comes from
  // `users.monthly_budget` on the server, which is the canonical source.
  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/spend", {
        params: { months: 1 },
      });
      setAnalytics(data);
    } catch (e) {
      setAnalytics(null);
    }
  }, []);

  useEffect(() => {
    if (user) loadAnalytics();
  }, [user, loadAnalytics]);

  // Bubble up state from SpaceCard so the other panels reload after a
  // create/join/leave without a full page refresh.
  const handleSpaceChange = useCallback(
    (next) => {
      setSpaceState(next);
      loadAnalytics();
    },
    [loadAnalytics],
  );

  if (!user) return null;

  const isSpace = !!spaceState?.isSpace;
  const isFlatmates = spaceState?.type === "flatmates";

  return (
    <section
      className="space-y-4 md:space-y-6"
      data-testid="shared-space-view"
    >
      {/* Always render the SpaceCard — primary entry point. */}
      <SpaceCard onChange={handleSpaceChange} />

      {/* The rest only appears once the user belongs to a space. */}
      {isSpace ? (
        <>
          <SharedSpaceOverview spaceState={spaceState} />

          {isFlatmates ? (
            <BillSplitPanel />
          ) : (
            <BudgetProgressPanel
              spent={analytics?.currentMonth?.total || 0}
              budget={
                // Pull from the analytics response (server-derived from
                // users.monthly_budget). Fall through to the user object
                // before defaulting to 9000 so we never under-budget a
                // family that bumped their plan limit.
                analytics?.budget ??
                user?.monthly_budget ??
                9000
              }
              monthLabel={analytics?.currentMonth?.label || ""}
              memberCount={spaceState?.memberCount || 1}
            />
          )}
        </>
      ) : null}
    </section>
  );
}
